import { fetchCapabilities, fetchSelectedCapabilities } from './capabilitiesApi';
import { fetchChat } from './chatApi';
import { fetchReports } from './reportsApi';
import { getProjectState } from './dashboard';
import { getSubCapabilityAssessmentData } from './assessment';
import { getDynamicAssessmentResponses } from './dynamicAssessmentApi';
import type { GetDynamicAssessmentResponsesRequest } from '../types/dynamicAssessment';
import {
  cachedClientFetch,
  cachedClientFetchSWR,
  clientCacheKey,
  CLIENT_DATA_CAPABILITIES_TTL_MS,
  CLIENT_DATA_DEFAULT_TTL_MS,
  CLIENT_DATA_SHORT_TTL_MS,
  invalidateClientDataCache,
  peekClientDataCache,
} from '../utils/clientProjectDataCache';

export {
  invalidateClientDataCache,
  peekClientDataCache,
  clearAllClientDataCache,
} from '../utils/clientProjectDataCache';

export type ProjectUserPayload = {
  userId: string | number;
  projectId: string | number;
};

export function invalidateClientProjectData(
  userId?: string | number | null,
  projectId?: string | number | null
) {
  invalidateClientDataCache({
    userId,
    projectId,
  });
}

export {
  invalidateAfterCapabilitySelectionChange,
  invalidateAfterAssessmentChange,
  invalidateAfterChatChange,
  invalidateAfterReportChange,
} from '../utils/clientProjectDataCache';

export async function loadProjectCapabilities(projectId: string | number) {
  const key = clientCacheKey(['capabilities', projectId]);
  return cachedClientFetch(
    key,
    () => fetchCapabilities(projectId),
    {
      ttlMs: CLIENT_DATA_CAPABILITIES_TTL_MS,
      tags: ['capabilities'],
      projectId,
    }
  );
}

export async function loadSelectedCapabilities(payload: ProjectUserPayload) {
  const key = clientCacheKey([
    'selected_caps',
    payload.userId,
    payload.projectId,
  ]);
  return cachedClientFetch(
    key,
    () => fetchSelectedCapabilities(payload),
    {
      ttlMs: CLIENT_DATA_DEFAULT_TTL_MS,
      tags: ['selectedCapabilities'],
      projectId: payload.projectId,
      userId: payload.userId,
    }
  );
}

export async function loadProjectState(payload: {
  userId: string | number;
  projectId: number[] | string[];
}) {
  const sortedIds = [...payload.projectId].map(String).sort().join(',');
  const key = clientCacheKey(['project_state', payload.userId, sortedIds]);
  return cachedClientFetch(
    key,
    () => getProjectState(payload),
    {
      ttlMs: CLIENT_DATA_DEFAULT_TTL_MS,
      tags: ['projectState'],
      userId: payload.userId,
      projectId: payload.projectId.length === 1 ? payload.projectId[0] : undefined,
    }
  );
}

export async function loadSubCapabilityAssessmentLegacy(payload: ProjectUserPayload) {
  const key = clientCacheKey([
    'assess_legacy',
    payload.userId,
    payload.projectId,
  ]);
  return cachedClientFetch(
    key,
    () => getSubCapabilityAssessmentData(payload),
    {
      ttlMs: CLIENT_DATA_DEFAULT_TTL_MS,
      tags: ['assessmentLegacy'],
      projectId: payload.projectId,
      userId: payload.userId,
    }
  );
}

export async function loadDynamicAssessmentResponsesCached(
  request: GetDynamicAssessmentResponsesRequest
) {
  const { userId, projectId, subjectId: subCapabilityId } = request;
  const key = clientCacheKey(['dyn_assess', userId, projectId, subCapabilityId]);

  return cachedClientFetch(
    key,
    () => getDynamicAssessmentResponses(request),
    {
      ttlMs: CLIENT_DATA_DEFAULT_TTL_MS,
      tags: ['dynamicAssessmentResponse'],
      userId,
      projectId,
    }
  );
}

export async function loadProjectChat(payload: ProjectUserPayload) {
  const key = clientCacheKey(['chat', payload.userId, payload.projectId]);
  return cachedClientFetch(
    key,
    () => fetchChat(payload),
    {
      ttlMs: CLIENT_DATA_SHORT_TTL_MS,
      tags: ['chat'],
      projectId: payload.projectId,
      userId: payload.userId,
    }
  );
}

export async function loadProjectReports(projectId: string | number) {
  const key = clientCacheKey(['reports', projectId]);
  return cachedClientFetch(
    key,
    () => fetchReports(projectId),
    {
      ttlMs: CLIENT_DATA_SHORT_TTL_MS,
      tags: ['reports'],
      projectId,
    }
  );
}

export type ClientPortalSnapshot = {
  chatHistory: unknown[];
  reports: unknown[];
  selectedCapabilitiesRes: Awaited<ReturnType<typeof fetchSelectedCapabilities>> | null;
  chatCount: number;
};

export async function loadClientPortalSnapshotSWR(payload: ProjectUserPayload) {
  const key = clientCacheKey(['portal_snapshot', payload.userId, payload.projectId]);
  return cachedClientFetchSWR(
    key,
    async (): Promise<ClientPortalSnapshot> => {
      const [chatHistory, reports, selectedCapabilitiesRes] = await Promise.all([
        loadProjectChat(payload).catch(() => []),
        loadProjectReports(payload.projectId).catch(() => []),
        loadSelectedCapabilities(payload).catch(() => null),
      ]);
      const count = Array.isArray(chatHistory) ? chatHistory.length : 0;
      return {
        chatHistory: Array.isArray(chatHistory) ? chatHistory : [],
        reports: Array.isArray(reports) ? reports : [],
        selectedCapabilitiesRes,
        chatCount: count,
      };
    },
    {
      ttlMs: CLIENT_DATA_SHORT_TTL_MS,
      tags: ['portalSnapshot'],
      projectId: payload.projectId,
      userId: payload.userId,
      revalidate: true,
    }
  );
}
